import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

const STATUSES = [
  { value: 'new', label: 'Neu' },
  { value: 'in_review', label: 'In Prüfung' },
  { value: 'accepted', label: 'Angenommen' },
  { value: 'rejected', label: 'Abgelehnt' }
];
const TYPE = { freiflaeche: 'Freifläche', acker: 'Acker', gewerbe: 'Gewerbefläche', dach: 'Dach', konversion: 'Konversionsfläche', sonstige: 'Sonstige' };
const OWN = { eigentuemer: 'Eigentümer', pachter: 'Pächter', makler: 'Makler', sonstige: 'Sonstige' };

const AdminLandOffers = () => {
  const [offers, setOffers] = useState([]);
  const [notes, setNotes] = useState({});
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/land-offers`);
      setOffers(r.data);
      setNotes(Object.fromEntries(r.data.map((o) => [o.id, o.admin_note || ''])));
    } catch (e) {
      toast.error('Flächenangebote konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await axios.put(`${API}/land-offers/${id}/status`, { status, admin_note: notes[id] ?? '' });
      toast.success('Status gespeichert');
      load();
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    }
  };

  const openFile = async (id, name) => {
    try {
      const r = await axios.get(`${API}/land-offers/${id}/files/${name}`, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch (e) {
      toast.error('Datei konnte nicht geladen werden');
    }
  };

  const shown = offers.filter((o) => filter === 'all' || o.status === filter);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-land-offers">
      <Link to="/bess/dashboard" className="text-sm text-slate-500 hover:underline">← Dashboard</Link>
      <h1 className="text-3xl font-bold mt-2 mb-1">Flächenangebote</h1>
      <p className="text-slate-600 mb-6">Über die Hauptseite eingereichte Flächen. Neue Angebote stehen auf „Neu“ und werden hier geprüft.</p>

      <div className="flex flex-wrap gap-2 mb-6">
        {[{ value: 'all', label: `Alle (${offers.length})` }, ...STATUSES.map((s) => ({ ...s, label: `${s.label} (${offers.filter((o) => o.status === s.value).length})` }))].map((f) => (
          <button key={f.value} onClick={() => setFilter(f.value)}
            className={`rounded-full border px-4 py-1 text-sm ${filter === f.value ? 'bg-slate-900 text-white' : 'bg-white text-slate-700'}`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? <p>Lade …</p> : shown.length === 0 ? <p className="text-slate-500">Keine Flächenangebote in dieser Ansicht.</p> : (
        <div className="space-y-4">
          {shown.map((o) => (
            <div key={o.id} className="rounded-xl border bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold">{o.location} · {o.area_size} {o.area_unit === 'ha' ? 'ha' : 'm²'} · {TYPE[o.area_type] || o.area_type}</h2>
                  <p className="text-sm text-slate-500">{new Date(o.created_at).toLocaleString('de-DE')} · {OWN[o.ownership] || o.ownership}</p>
                </div>
                <select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} className="rounded-md border px-3 py-1.5 text-sm">
                  {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <dl className="mt-3 grid grid-cols-1 gap-x-8 gap-y-1 text-sm sm:grid-cols-2">
                <div><dt className="inline text-slate-500">Kontakt: </dt><dd className="inline">{o.name}, <a className="underline" href={`mailto:${o.email}`}>{o.email}</a>{o.phone ? `, ${o.phone}` : ''}</dd></div>
                {o.parcel && <div><dt className="inline text-slate-500">Flurstück: </dt><dd className="inline">{o.parcel}</dd></div>}
                {o.grid_distance_km != null && <div><dt className="inline text-slate-500">Umspannwerk: </dt><dd className="inline">{o.grid_distance_km} km</dd></div>}
                {o.availability && <div><dt className="inline text-slate-500">Verfügbar: </dt><dd className="inline">{o.availability}</dd></div>}
              </dl>
              {(o.files || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2 text-sm">
                  <span className="text-slate-500">Anlagen:</span>
                  {o.files.map((f) => (
                    <button key={f.name} onClick={() => openFile(o.id, f.name)} className="rounded-md border px-2 py-0.5 hover:bg-slate-50">
                      {f.name} ({Math.round(f.size / 1024)} KB)
                    </button>
                  ))}
                </div>
              )}
              {o.folder && <p className="mt-2 text-xs text-slate-400">Ordner auf dem Server: land-offers/{o.folder}</p>}
              {o.message && <p className="mt-3 whitespace-pre-wrap rounded-md bg-slate-50 p-3 text-sm">{o.message}</p>}
              <div className="mt-3 flex gap-2">
                <input value={notes[o.id] ?? ''} onChange={(e) => setNotes({ ...notes, [o.id]: e.target.value })}
                  placeholder="Interne Notiz zur Prüfung" className="flex-1 rounded-md border px-3 py-1.5 text-sm" />
                <button onClick={() => setStatus(o.id, o.status)} className="rounded-md border px-3 py-1.5 text-sm hover:bg-slate-50">Notiz speichern</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminLandOffers;
